import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react'
import { User, Organization, AuthState } from '@/types'
import apiService from '@/services/api'
import { store, logoutAction } from '@/store'

// Reset all Redux slices so no user-scoped data (folder tree, files, search…)
// survives an identity change. Must be dispatched imperatively here: the
// components living under ProtectedApp unmount as soon as isAuthenticated
// flips, before any of their effects could run.
function resetAppData() {
  store.dispatch(logoutAction())
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>
  loginWithToken: (token: string, user: User, organization: Organization) => void
  logout: () => Promise<void>
  refreshToken: () => Promise<void>
  refreshUser: () => Promise<void>
  impersonate: (userId: number) => Promise<void>
  stopImpersonation: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

type AuthAction =
  | { type: 'LOGIN_START' }
  | {
      type: 'LOGIN_SUCCESS'
      payload: {
        user: User
        organization: Organization
        token: string
        impersonatorId: number | null
      }
    }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'TOKEN_REFRESH'; payload: { token: string; user?: User } }
  | { type: 'USER_REFRESH'; payload: { user: User } }
  | { type: 'SET_LOADING'; payload: boolean }

// Decode the impersonator_id claim from a JWT without pulling in a dependency.
// JWTs are three base64url segments — we only need the payload.
function readImpersonatorId(token: string | null): number | null {
  if (!token) return null
  try {
    const parts = token.split('.')
    if (parts.length < 2) return null
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4)
    const decoded = JSON.parse(atob(padded))
    const id = decoded?.impersonator_id
    return typeof id === 'number' ? id : null
  } catch {
    return null
  }
}

const initialToken = localStorage.getItem('auth_token')

const initialState: AuthState = {
  user: null,
  organization: null,
  token: initialToken,
  isAuthenticated: false,
  isLoading: true,
  impersonatorId: readImpersonatorId(initialToken),
}

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true }

    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        organization: action.payload.organization,
        token: action.payload.token,
        impersonatorId: action.payload.impersonatorId,
        isAuthenticated: true,
        isLoading: false,
      }

    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        organization: null,
        token: null,
        impersonatorId: null,
        isAuthenticated: false,
        isLoading: false,
      }

    case 'LOGOUT':
      return {
        ...state,
        user: null,
        organization: null,
        token: null,
        impersonatorId: null,
        isAuthenticated: false,
        isLoading: false,
      }

    case 'TOKEN_REFRESH':
      return {
        ...state,
        token: action.payload.token,
        impersonatorId: readImpersonatorId(action.payload.token),
        user: action.payload.user ?? state.user,
        organization: action.payload.user?.organization ?? state.organization,
      }

    case 'USER_REFRESH':
      return {
        ...state,
        user: action.payload.user,
        organization: action.payload.user.organization ?? state.organization,
      }

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      }

    default:
      return state
  }
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Check if user is already logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token')
      const storedUser = localStorage.getItem('user')

      if (token && storedUser) {
        try {
          const user = JSON.parse(storedUser)
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              token,
              user,
              organization: user.organization,
              impersonatorId: readImpersonatorId(token),
            },
          })

          // Revalidate the cached user in the background so stale
          // name/email/role data doesn't persist until the next login.
          try {
            const { user: freshUser } = await apiService.getMe()
            localStorage.setItem('user', JSON.stringify(freshUser))
            dispatch({ type: 'USER_REFRESH', payload: { user: freshUser } })
          } catch {
            // 401s are handled by the api interceptor; ignore other failures
          }
        } catch (error) {
          localStorage.removeItem('auth_token')
          localStorage.removeItem('user')
          dispatch({ type: 'LOGIN_FAILURE' })
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    }

    checkAuth()
  }, [])

  const loginWithToken = (token: string, user: User, organization: Organization) => {
    resetAppData()
    localStorage.setItem('auth_token', token)
    localStorage.setItem('user', JSON.stringify(user))
    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: { token, user, organization, impersonatorId: readImpersonatorId(token) },
    })
  }

  const login = async (email: string, password: string) => {
    dispatch({ type: 'LOGIN_START' })
    try {
      const response = await apiService.login(email, password)
      resetAppData()
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          ...response,
          impersonatorId: readImpersonatorId(response.token),
        },
      })
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' })
      throw error
    }
  }

  const logout = async () => {
    try {
      await apiService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    }
    dispatch({ type: 'LOGOUT' })
    resetAppData()
  }

  const refreshToken = async () => {
    try {
      const response = await apiService.refreshToken()
      dispatch({
        type: 'TOKEN_REFRESH',
        payload: response,
      })
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' })
      throw error
    }
  }

  const refreshUser = async () => {
    const { user } = await apiService.getMe()
    localStorage.setItem('user', JSON.stringify(user))
    dispatch({ type: 'USER_REFRESH', payload: { user } })
  }

  const impersonate = async (userId: number) => {
    const response = await apiService.impersonateUser(userId)
    resetAppData()
    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: {
        ...response,
        impersonatorId: readImpersonatorId(response.token),
      },
    })
  }

  const stopImpersonation = async () => {
    const response = await apiService.stopImpersonation()
    resetAppData()
    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: {
        ...response,
        impersonatorId: readImpersonatorId(response.token),
      },
    })
  }

  const value: AuthContextType = {
    ...state,
    login,
    loginWithToken,
    logout,
    refreshToken,
    refreshUser,
    impersonate,
    stopImpersonation,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
